import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { Popover, useTheme } from '@mui/material';
import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { LayoutFullWidth } from '@product-framework/index';
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import AssignJobDialog from '@product-framework/components/Dialogs/AssignJobDialog';
import SendMessageDialog from '@product-framework/components/Dialogs/SendMessageDialog';
import CancelJobDialog from '@product-framework/components/Dialogs/CancelJobDialog';
import RescheduleJobDialog from '@product-framework/components/Dialogs/RescheduleJobDialog';
import getClosestDateItemFromToday from '@ui-framework/helpers/getClosestDateItemFromToday';
import JobMap from '../../components/Map';
import MapLib from '../../components/Map/lib/MapLib';
import CenterMapButton from '../../components/Map/buttons/CenterMapButton';
import BackButton from '../../components/Map/buttons/BackButton';
import JobAndSPWidget from '../../components/Map/widget/index';
import ServiceProviderInfo from '../../components/Map/widget/ServiceProviderInfo';
import LegendsWidget from '../../components/Map/widget/Legends';

function ServiceJobMap() {
  const theme = useTheme();
  const { id: currentSJId } = useParams();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, get } = useDataProvider(provider);
  const snackbar = useSnackbar();
  const mapRef = useRef(null);
  const { formatMessage } = useLocalization();

  const defaults = {
    zoom: 10,
    speed: 8,
    lat: 40.56378846993402,
    lng: -99.9599284137568,
  };

  const defaultPagination = { limit: 10, page: 1, total: 0 };

  const dispatchStatusConfig = [
    {
      value: 'ALL',
      label: formatMessage({
        id: 'ALL_STATUS_FILTER',
        defaultMessage: 'All Providers',
      }),
    },
    {
      value: 'ACCEPTED_WITH_CONDITIONS',
      label: formatMessage({
        id: 'ACCEPTED_CONDITIONALLY_FILTER',
        defaultMessage: 'Accepted Conditionally',
      }),
    },
  ];

  const [map, setMap] = useState(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [spLoading, setSpLoading] = useState(true);
  const [jobInfo, setJobInfo] = useState(null);
  const [selectedServiceProvider, setSelectedServiceProvider] = useState(null);
  const [spPopOver, setSPPopOver] = useState({ open: false, top: 0, left: 0 });
  const [serviceProviders, setServiceProviders] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [dispatchEventStatus, setDispatchEventStatus] = useState(
    dispatchStatusConfig[0].value,
  );
  const [serviceDate, setServiceDate] = useState(dayjs());
  const [pagination, setPagination] = useState(defaultPagination);
  const [dialog, setDialog] = useState('NONE');
  const [mapId, setMapId] = useState('initial');

  const jobFetchUrl = `${getApiUrl()}/sxcl/v1/service-jobs/${currentSJId}`;

  const getServiceProvidersFetchUrl = () => {
    let url = `${getApiUrl()}/sxcl/v1/service-jobs/${currentSJId}/map?serviceDate=${dayjs(
      serviceDate,
    ).format('YYYY-MM-DD')}&lat=${jobInfo?.locationLat}&lng=${
      jobInfo?.locationLng
    }&radius=160934&limit=${defaultPagination?.limit}&page=${pagination?.page}`;

    if (searchText && searchText.length > 1) {
      url += `&name=${searchText}`;
    }
    if (
      dispatchEventStatus &&
      dispatchEventStatus !== dispatchStatusConfig[0].value
    ) {
      url += `&dispatchEventStatus=${dispatchEventStatus}`;
    }
    return url;
  };

  const onServiceProviderClick = (e, pageMap, spId, marker, spAddress) => {
    mapRef.current.marker.selectedServiceProvider = marker;

    const index = marker.getAttribute('data-index');
    const layerName = MapLib.serviceProviderServiceJobGetLayerName(index);

    mapRef.current.serviceProviderUnHighlightAllMarkers([spId]);
    mapRef.current.serviceProviderServiceJobUnHighlightAllLayers([spId]);

    MapLib.serviceProviderHighlightMarker(spId, spAddress.id);
    mapRef.current.serviceProviderServiceJobHighlightLayer(layerName);

    setSelectedServiceProvider({
      sp: mapRef?.current.serviceProviders.find(sp => sp.id === spId),
      spAddress,
    });

    setSPPopOver({
      open: true,
      left: e.clientX,
      top: e.clientY + 10,
    });
  };

  const fetchAndSetServiceJob = async () => {
    setJobLoading(true);
    const sjResponse = await get(jobFetchUrl);
    if (sjResponse.ok) {
      const currentJobInfo = sjResponse?.data;
      setJobInfo(currentJobInfo);
      const closestDate = getClosestDateItemFromToday({
        dates: currentJobInfo?.customerServiceRequests,
      })?.requestDate;

      let newServiceDate;
      if (currentJobInfo?.serviceJobStatus === 'SERVICE_PENDING') {
        newServiceDate = dayjs(currentJobInfo?.appointmentDate);
      } else if (closestDate && dayjs(closestDate).isValid()) {
        newServiceDate = dayjs(closestDate);
      } else {
        newServiceDate = dayjs();
      }
      setServiceDate(newServiceDate);
      mapRef?.current?.setJob({ job: currentJobInfo });
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={sjResponse?.errors} />,
      });
    }
    setJobLoading(false);
  };

  const fetchAndSetServiceProvider = async () => {
    setSpLoading(true);
    const serviceProviderFetchUrl = getServiceProvidersFetchUrl();
    const spResponse = await get(serviceProviderFetchUrl);
    if (spResponse.ok) {
      const { data, ...paginationData } = spResponse.data;
      setServiceProviders(data);
      setPagination(paginationData);
      mapRef?.current?.setServiceProviders({ serviceProviders: data });
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={spResponse?.errors} />,
      });
    }
    setSpLoading(false);
  };

  const renderJobOnMap = async () => {
    await fetchAndSetServiceJob();
    mapRef?.current?.renderJob();
  };

  const renderServiceProvidersOnMap = async () => {
    await fetchAndSetServiceProvider();
    mapRef?.current?.renderServiceProviders();
  };

  const onMapReady = async mapObj => {
    setMap(mapObj);
    mapRef.current = new MapLib({
      map: mapObj,
      zoom: defaults.zoom,
      speed: defaults.speed,
      theme,
      marker: {
        job: null,
        serviceProviders: [],
        selectedServiceProvider: null,
      },
      onServiceProviderClick,
    });
  };

  useEffect(() => {
    if (map) {
      renderJobOnMap();
    }
  }, [map, mapId]);

  useEffect(() => {
    if (jobInfo) {
      renderServiceProvidersOnMap();
    }
  }, [
    jobInfo,
    searchText,
    dispatchEventStatus,
    pagination?.page,
    serviceDate,
    mapId,
  ]);

  return (
    <LayoutFullWidth>
      <Popover
        open={spPopOver.open}
        onClose={() => {
          setSPPopOver({
            open: false,
            left: 0,
            top: 0,
          });

          const index =
            mapRef.current.marker.selectedServiceProvider?.getAttribute(
              'data-index',
            );
          const layerName = MapLib.serviceProviderServiceJobGetLayerName(index);
          mapRef?.current?.serviceProviderServiceJobUnHighlightLayer(layerName);
          mapRef.current.marker.selectedServiceProvider
            ?.getElementById('outer')
            ?.setAttribute('display', 'none');
        }}
        anchorReference="anchorPosition"
        anchorPosition={{ top: spPopOver.top, left: spPopOver.left }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}>
        <ServiceProviderInfo
          serviceProvider={selectedServiceProvider}
          currentJob={jobInfo}
          setDialog={setDialog}
        />
      </Popover>
      <JobMap onReady={mapObj => onMapReady(mapObj)} {...defaults} />
      <LegendsWidget />
      <BackButton />
      {!spLoading && !jobLoading && (
        <CenterMapButton
          map={map}
          lat={jobInfo?.locationLat}
          lng={jobInfo?.locationLng}
          zoom={defaults.zoom}
        />
      )}
      <JobAndSPWidget
        sx={{ zIndex: themeobj => themeobj.zIndex.drawer + 10 }}
        jobInfo={jobInfo}
        jobLoading={jobLoading}
        spLoading={spLoading}
        mapRef={mapRef}
        serviceProviders={serviceProviders}
        setActiveTab={setActiveTab}
        setSearchText={setSearchText}
        setDispatchEventStatus={setDispatchEventStatus}
        setServiceDate={setServiceDate}
        setPagination={setPagination}
        activeTab={activeTab}
        searchText={searchText}
        dispatchEventStatus={dispatchEventStatus}
        serviceDate={serviceDate}
        pagination={pagination}
        dispatchStatusConfig={dispatchStatusConfig}
        setDialog={setDialog}
      />
      {jobInfo && selectedServiceProvider && (
        <>
          <AssignJobDialog
            key={dialog === 'ASSIGN' ? 'ASSIGN1' : 'ASSIGN2'}
            open={dialog === 'ASSIGN'}
            onClose={refreshId => {
              setDialog('NONE');
              if (refreshId !== null) {
                setMapId(refreshId);
              }
            }}
            jobInfo={jobInfo}
            selectedServiceProvider={selectedServiceProvider}
          />
          <SendMessageDialog
            key={dialog === 'MESSAGE' ? 'MESSAGE1' : 'MESSAGE2'}
            open={dialog === 'MESSAGE'}
            onClose={() => setDialog('NONE')}
            jobId={jobInfo?.id}
            serviceProviderId={selectedServiceProvider?.sp?.id}
          />
        </>
      )}
      <CancelJobDialog
        key={dialog === 'CANCEL' ? 'CANCEL1' : 'CANCEL2'}
        open={dialog === 'CANCEL'}
        onClose={refreshId => {
          setDialog('NONE');
          if (refreshId !== null) {
            setMapId(refreshId);
          }
        }}
        recordVersion={jobInfo?.recordVersion}
        jobId={jobInfo?.id}
        jobNumber={jobInfo?.serviceJobNumber}
        jobStatus={jobInfo?.serviceJobStatus}
      />
      <RescheduleJobDialog
        key={dialog === 'RESCHEDULE' ? 'RESCHEDULE1' : 'RESCHEDULE2'}
        open={dialog === 'RESCHEDULE'}
        onClose={refreshId => {
          setDialog('NONE');
          if (refreshId !== null) {
            setMapId(refreshId);
          }
        }}
        initialData={jobInfo}
        recordVersion={jobInfo?.recordVersion}
        jobId={jobInfo?.id}
        jobNumber={jobInfo?.serviceJobNumber}
        jobStatus={jobInfo?.serviceJobStatus}
        providerId={jobInfo?.serviceProvider?.id}
        permissions={{
          customerServiceRequests: {
            requestDate: 'WRITE',
            afterHour: 'WRITE',
            beforeHour: 'WRITE',
          },
        }}
      />
    </LayoutFullWidth>
  );
}

export default observer(ServiceJobMap);
